import React, { useState } from 'react';
import api from '../../utils/api';

/**
 * BilingualField — renders side-by-side EN + ZH inputs with translate buttons.
 * Props:
 *   label        — display label (e.g. "Main Headline")
 *   fieldName    — key in the content state (e.g. "heroHeadline")
 *   value        — { en: string, zh: string }
 *   onChange     — fn(fieldName, lang, newValue)
 *   isTextarea   — bool, renders textarea instead of input
 *   rows         — number of textarea rows
 *   placeholder  — EN placeholder text
 */
export default function BilingualField({ label, fieldName, value = {}, onChange, isTextarea = false, rows = 2, placeholder = '' }) {
  const [translating, setTranslating] = useState(null); // 'en→zh' | 'zh→en' | null

  const enVal = value.en || '';
  const zhVal = value.zh || '';

  const handleTranslate = async (direction) => {
    const from = direction === 'en→zh' ? 'en' : 'zh-TW';
    const to   = direction === 'en→zh' ? 'zh-TW' : 'en';
    const text = direction === 'en→zh' ? enVal : zhVal;

    if (!text.trim()) return;
    setTranslating(direction);

    try {
      const res = await api.post('/translate', { text, from, to });
      const translated = res.data.translated;
      const targetLang = direction === 'en→zh' ? 'zh' : 'en';
      onChange(fieldName, targetLang, translated);
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setTranslating(null);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    fontSize: 14,
    fontFamily: 'inherit',
    resize: 'vertical',
    background: 'var(--surface)',
    color: 'var(--text)'
  };

  const renderInput = (lang, val, ph) => {
    if (isTextarea) {
      return (
        <textarea
          style={inputStyle}
          value={val}
          rows={rows}
          placeholder={ph}
          onChange={e => onChange(fieldName, lang, e.target.value)}
        />
      );
    }
    return (
      <input
        type="text"
        style={inputStyle}
        value={val}
        placeholder={ph}
        onChange={e => onChange(fieldName, lang, e.target.value)}
      />
    );
  };

  const btnStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid var(--border)',
    background: active ? 'var(--primary)' : 'var(--surface)',
    color: active ? '#fff' : 'var(--text-muted)',
    fontSize: 12,
    cursor: active ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    transition: 'all 0.2s'
  });

  return (
    <div className="form-group" style={{ marginBottom: 20 }}>
      <label className="form-label-style" style={{ marginBottom: 8, display: 'block' }}>
        {label}
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'flex-start' }}>

        {/* English Column */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>🇬🇧 English</span>
          </div>
          {renderInput('en', enVal, placeholder)}
        </div>

        {/* Translate Buttons Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 24, alignItems: 'center' }}>
          <button
            type="button"
            style={btnStyle(translating === 'en→zh')}
            disabled={!!translating}
            onClick={() => handleTranslate('en→zh')}
            title="Translate English → Chinese"
          >
            {translating === 'en→zh' ? '⏳' : '→ 中文'}
          </button>
          <button
            type="button"
            style={btnStyle(translating === 'zh→en')}
            disabled={!!translating}
            onClick={() => handleTranslate('zh→en')}
            title="Translate Chinese → English"
          >
            {translating === 'zh→en' ? '⏳' : 'EN ←'}
          </button>
        </div>

        {/* Chinese Column */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>🇭🇰 繁體中文</span>
          </div>
          {renderInput('zh', zhVal, '（中文翻譯）')}
        </div>

      </div>
    </div>
  );
}
